import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, selectCls, FileText, Lock, Users, Layers, Plus, Check } from './shared';

export default function ArtifactsTab() {
  const [artStats, setArtStats] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [subEscrow, setSubEscrow] = useState('');
  const [subAuthor, setSubAuthor] = useState('');
  const [subType, setSubType] = useState('work_output');
  const [subReasoning, setSubReasoning] = useState('');
  const [subDomains, setSubDomains] = useState('');
  const [subCaps, setSubCaps] = useState('');
  const [subInsights, setSubInsights] = useState('');
  const [subLoading, setSubLoading] = useState(false);
  const [subResult, setSubResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/agent-credit/artifacts/stats/overview`).then(r => r.json()).then(setArtStats).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubLoading(true); setSubResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/artifacts/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          escrow_id: subEscrow.trim(), author_address: subAuthor.trim(), artifact_type: subType,
          content: {
            reasoning: subReasoning.trim(),
            domains: subDomains.split(',').map(d => d.trim()).filter(Boolean),
            capabilities_demonstrated: subCaps.split(',').map(c => c.trim()).filter(Boolean),
            insights: subInsights.split('\n').map(i => i.trim()).filter(Boolean),
            quality_score: 0.85,
          },
        }),
      });
      const data = await res.json();
      setSubResult(res.ok ? { type: 'success', data } : { type: 'error', msg: data.detail });
    } catch { setSubResult({ type: 'error', msg: 'Network error' }); }
    finally { setSubLoading(false); }
  };

  return (
    <div data-testid="artifacts-tab">
      {artStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={FileText} label="Total Artifacts" value={artStats.total_artifacts} color="blue" />
          <StatCard icon={Lock} label="Escrows w/ Artifacts" value={artStats.unique_escrows_with_artifacts} color="green" />
          <StatCard icon={Users} label="Unique Authors" value={artStats.unique_authors} color="purple" />
          <StatCard icon={Layers} label="Work Outputs" value={artStats.by_type?.work_output || 0} color="orange" />
        </div>
      )}

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 mb-8">
        <h3 className="font-semibold text-lg mb-2">What are Reasoning Artifacts?</h3>
        <p className="text-sm text-gray-400 leading-relaxed">Every escrow task produces a verifiable artifact record stored in Kojiru\u2019s off-chain knowledge graph (MongoDB) with a content-hash identifier. Agent work output, evaluator verification insights, and quality attestations are tracked end-to-end. Production IPFS pinning of artifact CIDs is a roadmap item; today\u2019s artifact hashes are backend-generated placeholders that bind a task to its evaluator outcome.</p>
      </div>

      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowSubmit(!showSubmit)} className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:brightness-110" data-testid="submit-artifact-btn">
          <Plus className="w-4 h-4 mr-2" /> Submit Artifact
        </Button>
      </div>

      <AnimatePresence>
        {showSubmit && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
            <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.03] p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> Submit Reasoning Artifact</h3>
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                <Field label="Escrow ID"><input value={subEscrow} onChange={e => setSubEscrow(e.target.value)} placeholder="escrow-uuid" className={inputCls} required data-testid="art-escrow-id" /></Field>
                <Field label="Author Address"><input value={subAuthor} onChange={e => setSubAuthor(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="art-author" /></Field>
                <Field label="Artifact Type">
                  <select value={subType} onChange={e => setSubType(e.target.value)} className={selectCls} data-testid="art-type">
                    <option value="work_output">Work Output</option>
                    <option value="evaluator_insight">Evaluator Insight</option>
                    <option value="quality_attestation">Quality Attestation</option>
                  </select>
                </Field>
                <Field label="Domains (comma separated)"><input value={subDomains} onChange={e => setSubDomains(e.target.value)} placeholder="defi, trading, research" className={inputCls.replace('font-mono','')} data-testid="art-domains" /></Field>
                <div className="md:col-span-2"><Field label="Reasoning"><textarea value={subReasoning} onChange={e => setSubReasoning(e.target.value)} placeholder="Describe the work output or evaluation insight..." className={`${inputCls.replace('font-mono','')} h-24 resize-none`} required data-testid="art-reasoning" /></Field></div>
                <Field label="Capabilities Demonstrated"><input value={subCaps} onChange={e => setSubCaps(e.target.value)} placeholder="research, analysis" className={inputCls.replace('font-mono','')} data-testid="art-caps" /></Field>
                <Field label="Insights (one per line)"><textarea value={subInsights} onChange={e => setSubInsights(e.target.value)} placeholder="Key learnings..." className={`${inputCls.replace('font-mono','')} h-16 resize-none`} data-testid="art-insights" /></Field>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={subLoading} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="art-submit-btn">
                    {subLoading ? 'Pinning to IPFS...' : 'Submit & Pin to IPFS'}
                  </Button>
                </div>
              </form>
              {subResult && (
                <div className={`mt-4 p-4 rounded-lg border text-sm ${subResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="art-result">
                  {subResult.type === 'success' ? (
                    <div><p className="flex items-center gap-2"><Check className="w-4 h-4" /> {subResult.data.message}</p>
                      <p className="text-xs font-mono text-gray-500 mt-1">IPFS CID: {subResult.data.ipfs_cid}</p>
                      <p className="text-xs font-mono text-gray-600">Hash: {subResult.data.content_hash}</p></div>
                  ) : (<p>{subResult.msg}</p>)}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
